/* ════════════════════════════════════════════════════════════════════════════
   StudoX Flow — LERNWELTEN  ·  data-driven gamified curriculum
   ----------------------------------------------------------------------------
   This is the single source of truth for the learning games. It is PURE DATA:
   adding a new lesson = add a module object here; adding a whole subject = add a
   world. The game engine (ModulePlayer + games/*) renders any module by its
   `mode`. All content is ORIGINAL and written for StudoX (ages 8–16, German),
   inspired by open curricula but not copied from any source.

   A module's `mode` picks the game and defines the shape of its task data:
     mode "quiz"  → questions:[{ q, options:[…], answer:<idx>, explain }]
     mode "match" → prompt, pairs:[{ a, b }]            (tap-to-pair)
     mode "cloze" → prompt, items:[{ text:"… ⬚ …", answer, options:[…] }]
   difficulty: 1 = leicht · 2 = mittel · 3 = knifflig
   ════════════════════════════════════════════════════════════════════════════ */

import { EXPANSION_MODULES } from "./expansion";

export const WORLDS = [
  { id: "mathe",   name: "Mathe-Galaxie",     emoji: "🪐", color: "#8b5cf6", blurb: "Zahlen, Brüche & Logik im All" },
  { id: "deutsch", name: "Wortwald",          emoji: "🌳", color: "#22c55e", blurb: "Rechtschreibung & Sprache" },
  { id: "english", name: "Word City",         emoji: "🏙️", color: "#06b6d4", blurb: "English vocab & grammar" },
  { id: "natur",   name: "Forscher-Labor",    emoji: "🔬", color: "#ec4899", blurb: "Biologie, Physik & Natur" },
  { id: "info",    name: "Code-Nexus",        emoji: "💻", color: "#3b82f6", blurb: "Programmieren & Cybersecurity" },
  { id: "denken",  name: "Rätsel-Turm",       emoji: "🧠", color: "#f59e0b", blurb: "Logik, Muster & Wissen" },
  { id: "gesell",  name: "Zeitreise-Portal",  emoji: "🏛️", color: "#e11d48", blurb: "Geschichte, Geo & Gesellschaft" },
  { id: "wissen",  name: "Kosmos-Wissen",     emoji: "🌌", color: "#14b8a6", blurb: "Weltall, Tiere & Allgemeinwissen" },
  { id: "gesund",  name: "Vital-Oase",        emoji: "❤️", color: "#f43f5e", blurb: "Gesundheit, Ernährung & Mind" },
];

const BASE_MODULES = [
  /* ───────────────────────── MATHE-GALAXIE ───────────────────────── */
  {
    id: "mathe-grundrechnen", world: "mathe", title: "Turbo-Rechnen", subtitle: "Die vier Grundrechenarten",
    grade: "3.–5. Klasse", age: "8–11", difficulty: 1, mode: "quiz", xp: 60, coins: 12, badge: "⚡ Rechen-Rakete",
    goals: ["Kopfrechnen festigen", "+ − × ÷ sicher anwenden"],
    questions: [
      { q: "Ein Raumschiff hat 6 Reihen mit je 7 Sitzen. Wie viele Sitze?", options: ["42", "13", "48", "36"], answer: 0, explain: "6 × 7 = 42." },
      { q: "144 ÷ 12 = ?", options: ["12", "14", "11", "24"], answer: 0, explain: "12 × 12 = 144, also 144 ÷ 12 = 12." },
      { q: "Welche Rechnung ergibt 100?", options: ["25 × 4", "30 + 60", "9 × 11", "150 − 60"], answer: 0, explain: "25 × 4 = 100." },
      { q: "37 + 48 = ?", options: ["85", "75", "95", "84"], answer: 0, explain: "37 + 48 = 85 (37+40=77, +8=85)." },
      { q: "Halbiere 250 und addiere 25.", options: ["150", "125", "275", "100"], answer: 0, explain: "250 ÷ 2 = 125, + 25 = 150." },
      { q: "Was ist am größten?", options: ["8 × 8", "70", "9 × 7", "60 + 5"], answer: 0, explain: "8 × 8 = 64; 9 × 7 = 63; 65; 70 — Achtung: 70 ist größer! Richtig wäre 70.", trap: true },
    ],
  },
  {
    id: "mathe-brueche", world: "mathe", title: "Bruch-Werkstatt", subtitle: "Brüche erkennen & vergleichen",
    grade: "5.–7. Klasse", age: "10–13", difficulty: 2, mode: "match", xp: 80, coins: 16, badge: "🍰 Bruchmeister",
    goals: ["Brüche kürzen", "gleichwertige Brüche erkennen"],
    prompt: "Verbinde jeden Bruch mit seinem gekürzten oder gleichwertigen Partner.",
    pairs: [
      { a: "2/4", b: "1/2" }, { a: "3/9", b: "1/3" }, { a: "6/8", b: "3/4" },
      { a: "5/10", b: "1/2 ✦" }, { a: "4/12", b: "1/3 ✦" }, { a: "10/100", b: "1/10" },
    ],
  },
  {
    id: "mathe-prozent", world: "mathe", title: "Prozent-Power", subtitle: "Prozentrechnung im Alltag",
    grade: "6.–8. Klasse", age: "11–14", difficulty: 2, mode: "quiz", xp: 90, coins: 18, badge: "📈 Prozent-Profi",
    goals: ["Prozentwerte berechnen", "Rabatte & Anteile verstehen"],
    questions: [
      { q: "Ein Spiel kostet 40 €, 25 % Rabatt. Neuer Preis?", options: ["30 €", "32 €", "35 €", "15 €"], answer: 0, explain: "25 % von 40 = 10 €. 40 − 10 = 30 €." },
      { q: "Wie viel sind 10 % von 250?", options: ["25", "2,5", "50", "20"], answer: 0, explain: "10 % = ÷ 10 = 25." },
      { q: "In einer Klasse sind 20 Kinder, 60 % sind anwesend. Wie viele?", options: ["12", "14", "10", "16"], answer: 0, explain: "60 % von 20 = 12." },
      { q: "Aus 50 mach +20 %. Ergebnis?", options: ["60", "70", "55", "65"], answer: 0, explain: "20 % von 50 = 10, also 60." },
      { q: "Was ist mehr?", options: ["50 % von 80", "30 % von 100", "25 % von 120", "alle gleich"], answer: 0, explain: "40 vs 30 vs 30 → 50 % von 80 = 40 ist am meisten." },
    ],
  },

  /* ───────────────────────── WORTWALD (Deutsch) ───────────────────────── */
  {
    id: "deutsch-rechtschreibung", world: "deutsch", title: "Buchstaben-Schmiede", subtitle: "Richtig schreiben",
    grade: "3.–6. Klasse", age: "8–12", difficulty: 1, mode: "cloze", xp: 60, coins: 12, badge: "✒️ Schreibheld",
    goals: ["häufige Rechtschreibfehler vermeiden", "das/dass, ie/ei"],
    prompt: "Wähle das richtige Wort für die Lücke.",
    items: [
      { text: "Ich weiß, ⬚ du recht hast.", answer: "dass", options: ["dass", "das"] },
      { text: "⬚ Auto ist schnell.", answer: "Das", options: ["Das", "Dass"] },
      { text: "Wir spielen am ⬚.", answer: "Spielplatz", options: ["Spielplatz", "Spielpllatz"] },
      { text: "Sie ⬚ ein Lied.", answer: "singt", options: ["singt", "sinkt"] },
      { text: "Der Hund ist sehr ⬚.", answer: "treu", options: ["treu", "troi"] },
    ],
  },
  {
    id: "deutsch-wortarten", world: "deutsch", title: "Wortarten-Safari", subtitle: "Nomen, Verb, Adjektiv",
    grade: "4.–7. Klasse", age: "9–13", difficulty: 2, mode: "match", xp: 80, coins: 16, badge: "🦜 Wort-Jäger",
    goals: ["Wortarten unterscheiden"],
    prompt: "Ordne jedes Wort seiner Wortart zu.",
    pairs: [
      { a: "rennen", b: "Verb" }, { a: "Haus", b: "Nomen" }, { a: "schnell", b: "Adjektiv" },
      { a: "lachen", b: "Verb ✦" }, { a: "Sonne", b: "Nomen ✦" }, { a: "blau", b: "Adjektiv ✦" },
    ],
  },

  /* ───────────────────────── WORD CITY (Englisch) ───────────────────────── */
  {
    id: "english-vocab", world: "english", title: "Vocab Arena", subtitle: "Everyday English words",
    grade: "5.–8. Klasse", age: "10–14", difficulty: 1, mode: "match", xp: 70, coins: 14, badge: "🗽 Word Hero",
    goals: ["Grundwortschatz EN↔DE"],
    prompt: "Match each English word with its German meaning.",
    pairs: [
      { a: "apple", b: "Apfel" }, { a: "house", b: "Haus" }, { a: "to run", b: "rennen" },
      { a: "friend", b: "Freund" }, { a: "book", b: "Buch" }, { a: "water", b: "Wasser" },
    ],
  },
  {
    id: "english-grammar", world: "english", title: "Grammar Quest", subtitle: "Present simple & articles",
    grade: "6.–9. Klasse", age: "11–15", difficulty: 2, mode: "cloze", xp: 90, coins: 18, badge: "📘 Grammar Knight",
    goals: ["he/she/it + s", "a / an"],
    prompt: "Choose the correct word.",
    items: [
      { text: "She ⬚ to school every day.", answer: "goes", options: ["goes", "go"] },
      { text: "I have ⬚ apple.", answer: "an", options: ["an", "a"] },
      { text: "They ⬚ football on Sundays.", answer: "play", options: ["play", "plays"] },
      { text: "He ⬚ a cool game.", answer: "has", options: ["has", "have"] },
      { text: "This is ⬚ university.", answer: "a", options: ["a", "an"] },
    ],
  },

  /* ───────────────────────── FORSCHER-LABOR (Natur) ───────────────────────── */
  {
    id: "natur-koerper", world: "natur", title: "Körper-Mission", subtitle: "Der menschliche Körper",
    grade: "4.–7. Klasse", age: "9–13", difficulty: 1, mode: "quiz", xp: 70, coins: 14, badge: "🫀 Bio-Forscher",
    goals: ["Organe & ihre Aufgaben"],
    questions: [
      { q: "Welches Organ pumpt Blut durch den Körper?", options: ["Herz", "Lunge", "Magen", "Leber"], answer: 0, explain: "Das Herz pumpt das Blut." },
      { q: "Womit atmen wir?", options: ["Lunge", "Niere", "Darm", "Haut"], answer: 0, explain: "Die Lunge nimmt Sauerstoff auf." },
      { q: "Wie viele Knochen hat ein erwachsener Mensch ungefähr?", options: ["206", "150", "300", "99"], answer: 0, explain: "Etwa 206 Knochen." },
      { q: "Was schützt das Gehirn?", options: ["Der Schädel", "Die Rippen", "Die Wirbelsäule allein", "Die Haut"], answer: 0, explain: "Der Schädelknochen schützt das Gehirn." },
      { q: "Welcher Sinn sitzt in der Nase?", options: ["Riechen", "Hören", "Sehen", "Schmecken"], answer: 0, explain: "Die Nase ist für das Riechen zuständig." },
    ],
  },
  {
    id: "natur-physik", world: "natur", title: "Kräfte & Energie", subtitle: "Physik zum Anfassen",
    grade: "6.–9. Klasse", age: "11–15", difficulty: 2, mode: "quiz", xp: 90, coins: 18, badge: "🧲 Physik-Pionier",
    goals: ["Schwerkraft, Energieformen"],
    questions: [
      { q: "Warum fällt ein Apfel nach unten?", options: ["Schwerkraft", "Magnetismus", "Wind", "Reibung"], answer: 0, explain: "Die Schwerkraft (Gravitation) zieht ihn zur Erde." },
      { q: "Welche Energieform hat ein gespanntes Gummiband?", options: ["Spannenergie", "Wärmeenergie", "Lichtenergie", "Schallenergie"], answer: 0, explain: "Gespannt speichert es Spann-/Lageenergie." },
      { q: "Was bremst ein rollendes Auto langsam ab?", options: ["Reibung", "Schwerkraft nach oben", "Schall", "Licht"], answer: 0, explain: "Reibung wandelt Bewegung in Wärme um." },
      { q: "Womit misst man Temperatur?", options: ["Thermometer", "Barometer", "Tachometer", "Voltmeter"], answer: 0, explain: "Ein Thermometer misst Temperatur." },
      { q: "Licht ist …", options: ["sehr schnell", "langsamer als Schall", "unbeweglich", "nur nachts da"], answer: 0, explain: "Licht ist das Schnellste, das wir kennen." },
    ],
  },

  /* ───────────────────────── CODE-NEXUS (Informatik) ───────────────────────── */
  {
    id: "info-algorithmen", world: "info", title: "Algorithmus-Pfad", subtitle: "Schritt-für-Schritt denken",
    grade: "5.–9. Klasse", age: "10–15", difficulty: 2, mode: "quiz", xp: 90, coins: 18, badge: "🤖 Code-Denker",
    goals: ["Was ist ein Algorithmus?", "Schleifen & Bedingungen"],
    questions: [
      { q: "Was ist ein Algorithmus?", options: ["Eine Schritt-für-Schritt-Anleitung", "Ein Computervirus", "Ein Kabel", "Ein Bildschirm"], answer: 0, explain: "Eine eindeutige Folge von Schritten zur Lösung." },
      { q: "Was macht eine Schleife (loop)?", options: ["wiederholt Schritte", "löscht Dateien", "schaltet den PC aus", "malt Bilder"], answer: 0, explain: "Eine Schleife wiederholt Anweisungen." },
      { q: "WENN es regnet, DANN nimm einen Schirm. Das ist eine …", options: ["Bedingung", "Schleife", "Variable", "Funktion"], answer: 0, explain: "Eine WENN-DANN-Bedingung (if)." },
      { q: "Was speichert einen Wert, z. B. punkte = 5?", options: ["Variable", "Schleife", "Bildschirm", "Maus"], answer: 0, explain: "Eine Variable speichert Werte." },
      { q: "Reihenfolge zählt bei Algorithmen …", options: ["immer", "nie", "nur sonntags", "nur bei Spielen"], answer: 0, explain: "Die richtige Reihenfolge ist entscheidend." },
    ],
  },
  {
    id: "info-cyber", world: "info", title: "Cyber-Schild", subtitle: "Sicher im Internet",
    grade: "5.–10. Klasse", age: "10–16", difficulty: 1, mode: "quiz", xp: 80, coins: 16, badge: "🛡️ Cyber-Wächter",
    goals: ["Passwörter, Phishing, Privatsphäre"],
    questions: [
      { q: "Welches Passwort ist am sichersten?", options: ["7!Kva#mZ2q", "123456", "passwort", "dein Name"], answer: 0, explain: "Lang, zufällig, mit Sonderzeichen." },
      { q: "Eine Mail sagt: 'Klicke hier, sonst wird dein Konto gelöscht!'. Was tun?", options: ["Nicht klicken, misstrauisch sein", "Sofort klicken", "Passwort eingeben", "Weiterleiten an alle"], answer: 0, explain: "Klassisches Phishing — Druck & Drohung sind Warnzeichen." },
      { q: "Was solltest du NICHT öffentlich posten?", options: ["Deine Adresse", "Ein Lieblingstier", "Eine Lieblingsfarbe", "Ein Hobby"], answer: 0, explain: "Private Daten wie Adresse gehören nicht ins Netz." },
      { q: "Was ist 2-Faktor-Authentifizierung?", options: ["ein 2. Sicherheits-Schritt beim Login", "zwei Bildschirme", "doppeltes Internet", "ein Spiel"], answer: 0, explain: "Ein zweiter Code/Schritt schützt zusätzlich." },
      { q: "Ein Fremder im Chat fragt nach deinem echten Namen & Schule. Du …", options: ["gibst nichts preis & sagst es Erwachsenen", "antwortest sofort", "schickst ein Foto", "triffst dich allein"], answer: 0, explain: "Persönliches nie an Fremde — hol dir Hilfe." },
    ],
  },

  /* ───────────────────────── RÄTSEL-TURM (Denken) ───────────────────────── */
  {
    id: "denken-logik", world: "denken", title: "Logik-Labyrinth", subtitle: "Schlau kombinieren",
    grade: "ab 4. Klasse", age: "9–16", difficulty: 2, mode: "quiz", xp: 85, coins: 17, badge: "🧩 Logik-Lord",
    goals: ["Muster & Schlussfolgern"],
    questions: [
      { q: "2, 4, 8, 16, … Wie geht es weiter?", options: ["32", "20", "24", "18"], answer: 0, explain: "Jede Zahl wird verdoppelt." },
      { q: "Alle Katzen sind Tiere. Minki ist eine Katze. Also ist Minki …", options: ["ein Tier", "ein Hund", "ein Auto", "unbekannt"], answer: 0, explain: "Logischer Schluss: Minki ist ein Tier." },
      { q: "Welches Wort passt nicht?", options: ["Banane", "Apfel", "Karotte", "Birne"], answer: 2, explain: "Karotte ist Gemüse, der Rest Obst." },
      { q: "Finn ist älter als Mia, Mia älter als Tom. Wer ist am jüngsten?", options: ["Tom", "Finn", "Mia", "gleich alt"], answer: 0, explain: "Finn > Mia > Tom → Tom ist am jüngsten." },
      { q: "Dreieck, Quadrat, Fünfeck, … Was kommt?", options: ["Sechseck", "Kreis", "Linie", "Punkt"], answer: 0, explain: "Die Eckenzahl steigt: 3, 4, 5, 6." },
    ],
  },

  /* ───────── WAVE 2: new modes (Tempo / Memory) + new worlds ───────── */
  {
    id: "mathe-tempo", world: "mathe", title: "Blitz-Rechnen", subtitle: "Kopfrechnen gegen die Uhr",
    grade: "4.–7. Klasse", age: "9–13", difficulty: 2, mode: "tempo", xp: 85, coins: 17, badge: "⏱️ Blitzhirn",
    goals: ["Rechentempo steigern", "automatisiertes Kopfrechnen"],
    questions: [
      { q: "7 × 8", options: ["56", "54", "63", "48"], answer: 0 },
      { q: "12 + 19", options: ["31", "29", "32", "30"], answer: 0 },
      { q: "81 ÷ 9", options: ["9", "8", "7", "11"], answer: 0 },
      { q: "100 − 37", options: ["63", "73", "67", "57"], answer: 0 },
      { q: "6 × 6", options: ["36", "30", "42", "32"], answer: 0 },
      { q: "45 + 45", options: ["90", "80", "95", "100"], answer: 0 },
      { q: "144 ÷ 12", options: ["12", "11", "14", "13"], answer: 0 },
      { q: "9 × 4", options: ["36", "32", "40", "35"], answer: 0 },
    ],
  },
  {
    id: "english-memory", world: "english", title: "Memory Match", subtitle: "Flip & pair the vocab",
    grade: "5.–8. Klasse", age: "10–14", difficulty: 1, mode: "memory", xp: 75, coins: 15, badge: "🃏 Memory Master",
    goals: ["Wortschatz visuell festigen"],
    prompt: "Finde die englischen Wörter und ihre deutschen Partner.",
    pairs: [
      { a: "cat", b: "Katze" }, { a: "dog", b: "Hund" }, { a: "sun", b: "Sonne" },
      { a: "tree", b: "Baum" }, { a: "star", b: "Stern" }, { a: "fish", b: "Fisch" },
    ],
  },
  {
    id: "gesell-geschichte", world: "gesell", title: "Zeitreise", subtitle: "Große Momente der Geschichte",
    grade: "5.–9. Klasse", age: "10–15", difficulty: 2, mode: "quiz", xp: 90, coins: 18, badge: "🏛️ Zeitreisender",
    goals: ["historische Einordnung", "Schlüsselereignisse"],
    questions: [
      { q: "Wer entdeckte 1492 für Europa den Seeweg nach Amerika?", options: ["Christoph Kolumbus", "Marco Polo", "Magellan", "Vasco da Gama"], answer: 0, explain: "Kolumbus erreichte 1492 die Karibik." },
      { q: "Welche Mauer fiel 1989?", options: ["Berliner Mauer", "Chinesische Mauer", "Hadrianswall", "Stadtmauer Wien"], answer: 0, explain: "Der Fall der Berliner Mauer 1989." },
      { q: "Wer erfand den Buchdruck mit beweglichen Lettern (um 1450)?", options: ["Johannes Gutenberg", "Albert Einstein", "Leonardo da Vinci", "Nikola Tesla"], answer: 0, explain: "Gutenberg revolutionierte das Drucken." },
      { q: "Die alten Pyramiden von Gizeh stehen in …", options: ["Ägypten", "Griechenland", "Mexiko", "Irak"], answer: 0, explain: "Die Pyramiden stehen in Ägypten." },
      { q: "Wie hießen die Ritter-Burgen-Zeit grob?", options: ["Mittelalter", "Steinzeit", "Neuzeit", "Antike"], answer: 0, explain: "Burgen & Ritter prägen das Mittelalter." },
    ],
  },
  {
    id: "wissen-laender", world: "wissen", title: "Hauptstadt-Held", subtitle: "Länder & ihre Hauptstädte",
    grade: "4.–8. Klasse", age: "9–14", difficulty: 1, mode: "match", xp: 75, coins: 15, badge: "🗺️ Geo-Genie",
    goals: ["Länder ↔ Hauptstädte"],
    prompt: "Ordne jedem Land seine Hauptstadt zu.",
    pairs: [
      { a: "Deutschland", b: "Berlin" }, { a: "Österreich", b: "Wien" }, { a: "Frankreich", b: "Paris" },
      { a: "Italien", b: "Rom" }, { a: "Spanien", b: "Madrid" }, { a: "Schweiz", b: "Bern" },
    ],
  },
  {
    id: "wissen-weltall", world: "wissen", title: "Kosmos-Quiz", subtitle: "Reise durch das Weltall",
    grade: "4.–8. Klasse", age: "9–14", difficulty: 1, mode: "quiz", xp: 80, coins: 16, badge: "🚀 Sternenkind",
    goals: ["Planeten & Weltraum-Basics"],
    questions: [
      { q: "Welcher Planet ist der Sonne am nächsten?", options: ["Merkur", "Erde", "Mars", "Venus"], answer: 0, explain: "Merkur ist der innerste Planet." },
      { q: "Welcher ist der größte Planet?", options: ["Jupiter", "Saturn", "Erde", "Mars"], answer: 0, explain: "Jupiter ist der größte Planet." },
      { q: "Was umkreist die Erde?", options: ["Der Mond", "Die Sonne", "Der Mars", "Jupiter"], answer: 0, explain: "Der Mond umkreist die Erde." },
      { q: "Wie heißt unsere Galaxie?", options: ["Milchstraße", "Andromeda", "Sonnensystem", "Orion"], answer: 0, explain: "Wir leben in der Milchstraße." },
      { q: "Was ist ein Stern wie unsere Sonne?", options: ["Eine riesige Gaskugel", "Ein Felsbrocken", "Ein Planet", "Ein Komet"], answer: 0, explain: "Sterne sind glühende Gaskugeln." },
    ],
  },
  {
    id: "wissen-tiere", world: "wissen", title: "Tier-Memory", subtitle: "Tiere & ihr Zuhause",
    grade: "3.–6. Klasse", age: "8–12", difficulty: 1, mode: "memory", xp: 70, coins: 14, badge: "🦁 Tier-Kenner",
    goals: ["Tiere ihren Lebensräumen zuordnen"],
    prompt: "Finde jedes Tier und seinen Lebensraum.",
    pairs: [
      { a: "🐠 Fisch", b: "Meer" }, { a: "🦅 Adler", b: "Berge" }, { a: "🐪 Kamel", b: "Wüste" },
      { a: "🐻 Bär", b: "Wald" }, { a: "🐧 Pinguin", b: "Antarktis" }, { a: "🦒 Giraffe", b: "Savanne" },
    ],
  },
  {
    id: "gesund-ernaehrung", world: "gesund", title: "Power-Teller", subtitle: "Gesunde Ernährung",
    grade: "3.–7. Klasse", age: "8–13", difficulty: 1, mode: "quiz", xp: 70, coins: 14, badge: "🥗 Ernährungs-Profi",
    goals: ["ausgewogene Ernährung verstehen"],
    questions: [
      { q: "Was solltest du am meisten essen?", options: ["Obst & Gemüse", "Süßigkeiten", "Chips", "Limo"], answer: 0, explain: "Obst & Gemüse bilden die Basis." },
      { q: "Was ist das beste Getränk für jeden Tag?", options: ["Wasser", "Energy-Drink", "Cola", "Eistee"], answer: 0, explain: "Wasser ist das gesündeste Getränk." },
      { q: "Wofür ist Kalzium (in Milch) gut?", options: ["Knochen", "Haare färben", "schneller laufen", "besser sehen im Dunkeln"], answer: 0, explain: "Kalzium stärkt Knochen & Zähne." },
      { q: "Wie viele Mahlzeiten sind ein guter Rhythmus?", options: ["3 Haupt + kleine Snacks", "nur 1 große", "ständig naschen", "gar nicht frühstücken"], answer: 0, explain: "Regelmäßige Mahlzeiten geben Energie." },
      { q: "Vollkornbrot ist … als Weißbrot.", options: ["sättigender & gesünder", "ungesünder", "gleich", "nur teurer"], answer: 0, explain: "Vollkorn hält länger satt." },
    ],
  },
  {
    id: "gesund-mind", world: "gesund", title: "Stark im Kopf", subtitle: "Mental Health Basics",
    grade: "5.–10. Klasse", age: "10–16", difficulty: 1, mode: "quiz", xp: 80, coins: 16, badge: "🧘 Seelenstark",
    goals: ["Gefühle, Stress & Hilfe holen"],
    questions: [
      { q: "Du bist gestresst vor einer Prüfung. Was hilft?", options: ["Tief durchatmen & Pausen planen", "gar nicht schlafen", "alles aufschieben", "dich runtermachen"], answer: 0, explain: "Atmung + Pausen senken Stress." },
      { q: "Ein Freund ist traurig. Was ist gut?", options: ["Zuhören & da sein", "Auslachen", "ignorieren", "Thema wechseln"], answer: 0, explain: "Zuhören hilft am meisten." },
      { q: "Es geht dir länger schlecht. Du …", options: ["redest mit einer Vertrauensperson", "behältst alles für dich", "tust so als wär nichts", "gibst dir die Schuld"], answer: 0, explain: "Hilfe holen ist stark, nicht schwach. (help.studox.eu)" },
      { q: "Wie viel Schlaf brauchen Jugendliche etwa?", options: ["8–10 Stunden", "3 Stunden", "egal", "20 Stunden"], answer: 0, explain: "Schlaf ist Treibstoff fürs Gehirn." },
      { q: "Soziale Medien machen dich fertig. Gut ist …", options: ["bewusste Pausen & entfolgen", "noch mehr scrollen", "alles glauben", "dich vergleichen"], answer: 0, explain: "Medien-Pausen schützen deine Stimmung." },
    ],
  },
];

/* THE curriculum: hand-crafted base set + the age-banded expansion engine
   (procedural math/logic + authored knowledge banks). Hundreds of modules,
   every one age-tagged. */
export const MODULES = [...BASE_MODULES, ...EXPANSION_MODULES];

/** All modules for one world. */
export const modulesOfWorld = (worldId) => MODULES.filter((m) => m.world === worldId);
/** A single module by id. */
export const moduleById = (id) => MODULES.find((id2) => id2.id === id) || null;
/** Count of tasks in a module (for time/XP display). */
export const taskCount = (m) => (m.questions?.length || m.pairs?.length || m.items?.length || 0);
/** Does a module fit a learner age? (parses "8–11"-style ranges, ±1 tolerance) */
export function moduleFitsAge(m, age) {
  if (!age) return true;
  const n = (String(m.age || "").match(/\d+/g) || []).map(Number);
  if (!n.length) return true;
  const lo = n[0], hi = n[1] ?? n[0];
  return age >= lo - 1 && age <= hi + 1;
}
